/* eslint-disable no-nested-ternary */
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ControlledCheckbox, ControlNumberField } from 'components/Form';
import { ControlledSwitch } from 'components/Form/ControlledSwitch';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { BadgeWithTooltip } from 'components/WithTooltip/BadgeWithTooltip';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimWorkOrderOtherDistributionData, BimWorkOrderOtherPriceCostData } from 'gql/graphql';
import { GridItem, Grid, IconAlert, Informer, IconQuestion, IconWarning, TextField } from 'uikit';
import { formatNumberToRu } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import type React from 'react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormSetValue, UseFormWatch, UseFormSetError, UseFormClearErrors, Control } from 'react-hook-form';
import { useFieldArray, useWatch } from 'react-hook-form';

import { useBimCalcWorkOrderOtherDistribution } from '../../../hooks/useBimCalcWorkOrderOtherDistribution';
import {
    getConstructionObjectInputs,
    getConstructionObjectInputsWOChange,
} from '../../../hooks/useBimMethodsDistributionOther';
import { useBimWorkOrderChangeOtherCalculation } from '../../../hooks/useBimWorkOrderChangeOtherCalculation';

import Style from './BimDistributionOtherAllocation.module.css';

interface IBimDistributionOtherAllocation {
    control: Control;
    distributionData: BimWorkOrderOtherDistributionData | undefined | null;
    setValue?: UseFormSetValue<any>;
    watch?: UseFormWatch<any>;
    workOrderOtherPriceId?: number;
    contractOtherCostId?: number;
    setError?: UseFormSetError<any>;
    clearErrors?: UseFormClearErrors<any>;
    isWorkOrderChange?: boolean;
    workOrderChangeCompositeIds?: string;
    isCalculating?: boolean;
}

interface IObjectError {
    id: number;
    messages: string[];
}

export const BimDistributionOtherAllocation: React.FC<IBimDistributionOtherAllocation> = (props) => {
    const {
        control,
        distributionData,
        setValue,
        watch,
        workOrderOtherPriceId,
        contractOtherCostId,
        setError,
        clearErrors,
        isWorkOrderChange,
        workOrderChangeCompositeIds,
        isCalculating,
    } = props;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'objectDistribution',
    });

    const [isSystemCalculation, setIsSystemCalculation] = useState(distributionData?.limit?.isSystemCalculation);
    const [includedCost, setIncludedCost] = useState(distributionData?.limit?.includedCost);
    const [objectDistributionSum, setObjectDistributionSum] = useState(0);
    const [checkedIds, setCheckedIds] = useState<number[]>(
        distributionData?.objectDistribution?.filter((o) => o.isIncluded)?.map((o) => o.workOrderObjectId) || [],
    );
    const [errors, setErrors] = useState<IObjectError[] | undefined>(
        distributionData?.objectDistribution
            ?.filter((o) => o.hasErrors)
            ?.map((o) => ({ id: o.workOrderObjectId, messages: o.errors || [] })),
    );

    const isHasCheckedIds = useMemo(() => !checkedIds.length, [checkedIds.length]);

    const isSystemCalculationWatch = useWatch({
        control,
        name: 'isSystemCalculation',
    });

    const onCalculation = useCallback(
        (items: BimWorkOrderOtherPriceCostData[]) => {
            const currentErrors: IObjectError[] = [];

            distributionData?.objectDistribution?.forEach((objectDistributionItem, index) => {
                const item = items.find((o) => o.workOrderObjectId === objectDistributionItem.workOrderObjectId);

                const keyToUpdate = `objectDistribution.${index}.cost`;

                if (item) {
                    setValue?.(keyToUpdate, item.others);

                    if (item.hasErrors) {
                        currentErrors.push({ id: item.workOrderObjectId, messages: item.errors || [] });
                    }
                } else {
                    setValue?.(keyToUpdate, '');
                }
            });

            setErrors(currentErrors);
        },
        [distributionData?.objectDistribution, setValue],
    );

    const onError = useCallback(() => {
        setValue?.('isSystemCalculation', false);
    }, [setValue]);

    const { bimCalcWorkOrderOtherDistributionStatus, bimCalcWorkOrderOtherDistributionRequest } =
        useBimCalcWorkOrderOtherDistribution({ workOrderOtherPriceId, onCalculation, onError });

    const { bimCalcWorkOrderChangeDistributionStatus, bimCalcWorkOrderChangeDistributionRequest } =
        useBimWorkOrderChangeOtherCalculation({ workOrderChangeCompositeIds, onCalculation, onError });

    useEffect(() => {
        const subscription = watch?.((value, { name, type }) => {
            const doCalcRequest = () => {
                if (!isWorkOrderChange) {
                    const workOrderObjectIds = getConstructionObjectInputs(value?.objectDistribution || []).map(
                        // TODO:
                        // @ts-ignore
                        (item) => Number(item.id),
                    );

                    if (!workOrderObjectIds.length) {
                        setValue?.('isSystemCalculation', false);

                        return;
                    }

                    bimCalcWorkOrderOtherDistributionRequest({
                        includedCost: replacerCost(value.includedCost),
                        workOrderObjectIds,
                        contractOtherCostId,
                    });
                }

                if (isWorkOrderChange) {
                    bimCalcWorkOrderChangeDistributionRequest({
                        includedCost: replacerCost(value.includedCost),
                        contractOtherCostId,
                        // TODO:
                        // @ts-ignore
                        ids: getConstructionObjectInputsWOChange(value?.objectDistribution || []).map((i) => i.id),
                    });
                }
            };

            // TODO: для изма ключи должны быть составные. workOrderObjectId + workOrderChangeObjectId
            // зарефакторить в будущем. пока вводим второй хелпер для поиска ключей из составного
            if (name?.startsWith('objectDistribution')) {
                const inputs = isWorkOrderChange
                    ? getConstructionObjectInputsWOChange(value?.objectDistribution || [])
                    : getConstructionObjectInputs(value?.objectDistribution || []);

                // TODO:ts
                const distributionSum = (inputs as any)?.reduce(
                    (sum: number, val: any) => sum + replacerCost(val.cost),
                    0,
                );

                setObjectDistributionSum(distributionSum);
                // TODO:
                // @ts-ignore
                setCheckedIds(inputs.map((i) => i.id));

                if (isSystemCalculation && type === 'change') {
                    doCalcRequest();
                }
            }

            if (name === 'includedCost') {
                setIncludedCost(replacerCost(value.includedCost));

                if (type === 'change' && value.isSystemCalculation) {
                    doCalcRequest();
                }
            }

            if (name === 'isSystemCalculation') {
                setIsSystemCalculation(value.isSystemCalculation);

                if (type === 'change') {
                    if (value.isSystemCalculation) {
                        doCalcRequest();
                    } else {
                        setErrors(undefined);
                    }
                }
            }
        });

        return () => subscription?.unsubscribe();
    }, [
        bimCalcWorkOrderOtherDistributionRequest,
        contractOtherCostId,
        watch,
        isSystemCalculation,
        isWorkOrderChange,
        bimCalcWorkOrderChangeDistributionRequest,
        setValue,
    ]);

    useEffect(() => {
        const objectDistribution = distributionData?.objectDistribution;
        const systemCalculation = distributionData?.limit?.isSystemCalculation;

        setValue?.('isSystemCalculation', systemCalculation);

        if (objectDistribution) {
            objectDistribution.forEach((distribution) => {
                append({
                    [`${distribution.workOrderObjectId}`]: distribution.isIncluded,
                    cost: distribution.cost,
                });
            });
        }
    }, [append, distributionData, setValue]);

    const isCalcPerforming = useMemo(
        () =>
            bimCalcWorkOrderOtherDistributionStatus === RequestStatus.FETCHING ||
            bimCalcWorkOrderChangeDistributionStatus === RequestStatus.FETCHING,
        [bimCalcWorkOrderChangeDistributionStatus, bimCalcWorkOrderOtherDistributionStatus],
    );

    const areSumsNotEqual = useMemo(
        () => !isSystemCalculation && objectDistributionSum !== includedCost,
        [includedCost, isSystemCalculation, objectDistributionSum],
    );

    const tooltipTitle = useMemo(
        () => `Сумма лимитов / Включено в ${isWorkOrderChange ? 'Изм к НЗ' : 'НЗ'}`,
        [isWorkOrderChange],
    );

    useEffect(() => {
        if (!areSumsNotEqual) {
            distributionData?.objectDistribution?.forEach((o, index) => {
                if (errors?.find((e) => e.id === o.workOrderObjectId)) {
                    setError?.(`objectDistribution.${index}.cost`, {});
                } else {
                    clearErrors?.(`objectDistribution.${index}.cost`);
                }
            });
        }
    }, [areSumsNotEqual, clearErrors, distributionData?.objectDistribution, errors, isSystemCalculation, setError]);

    useEffect(() => {
        if (areSumsNotEqual) {
            distributionData?.objectDistribution?.forEach((o, index) => {
                if (checkedIds.includes(o.workOrderObjectId)) {
                    setError?.(`objectDistribution.${index}.cost`, {});
                } else {
                    clearErrors?.(`objectDistribution.${index}.cost`);
                }
            });
        }
    }, [areSumsNotEqual, checkedIds, clearErrors, distributionData?.objectDistribution, setError]);

    // clean up array fields
    useEffect(
        () => () => {
            remove();
        },
        [remove],
    );

    const currentMainSum = useMemo(
        () =>
            !isCalculating && isSystemCalculationWatch
                ? formatNumberToRu(objectDistributionSum, 0)
                : formatNumberToRu(includedCost, 0),
        [isCalculating, isSystemCalculationWatch, objectDistributionSum, includedCost],
    );

    return (
        <>
            {areSumsNotEqual && (
                <Informer
                    status="alert"
                    label={`Сумма лимитов должна быть равна сумме включенной в ${
                        isWorkOrderChange ? 'Изм к НЗ' : 'НЗ'
                    }`}
                    icon={IconAlert}
                    className={Style.distributionOtherAllocationInformer}
                />
            )}
            <div className={Style.distributionOtherAllocationPanel}>
                <ComponentWithTooltip
                    tooltipText={
                        isHasCheckedIds ? 'Для автоматического расчета должен быть выбран хотя бы один объект' : ''
                    }
                >
                    <ControlledSwitch
                        size="s"
                        label="Распределить лимиты автоматически"
                        control={control}
                        name="isSystemCalculation"
                        disabled={isCalcPerforming || isHasCheckedIds}
                    />
                </ComponentWithTooltip>
                <div className={Style.distributionOtherAllocationSums}>
                    <span
                        className={
                            areSumsNotEqual
                                ? Style.distributionOtherAllocationLimitsSumAlert
                                : Style.distributionOtherAllocationLimitsSum
                        }
                    >
                        {formatNumberToRu(objectDistributionSum, 0)} ₽
                    </span>
                    / {currentMainSum} ₽
                </div>
                <TooltipWrapper message={tooltipTitle} className={Style.distributionOtherAllocationQuestion}>
                    <IconQuestion size="s" />
                </TooltipWrapper>
            </div>
            <Grid rowGap="xs" cols="3" gap="xs" className={Style.distributionOtherAllocationGrid}>
                <GridItem col="2" className={Style.distributionOtherAllocationGridHeader}>
                    Объект
                </GridItem>
                <GridItem className={Style.distributionOtherAllocationGridHeader}>Сумма, руб. без НДС</GridItem>
                {fields.map((item, index) => {
                    const distributionId = distributionData?.objectDistribution?.[index]?.workOrderObjectId;
                    const checkboxLabel = distributionData?.objectDistribution?.[index]?.name;
                    const currentErrors = errors?.find((e) => e.id === distributionId)?.messages;

                    return (
                        <Fragment key={item.id}>
                            <GridItem key={`${item.id}-1`} col="2">
                                <ControlledCheckbox
                                    key={`${item.id}-1-check`}
                                    name={`objectDistribution.${index}.${distributionId}`}
                                    label={checkboxLabel ?? ''}
                                    size="m"
                                    control={control}
                                    disabled={isCalcPerforming}
                                />
                            </GridItem>
                            <GridItem key={`${item.id}-2`}>
                                {currentErrors?.length ? (
                                    <BadgeWithTooltip
                                        messages={currentErrors}
                                        size="l"
                                        badgeSize="s"
                                        label="Ошибка"
                                        status="error"
                                        iconLeft={IconWarning}
                                    />
                                ) : !checkedIds.includes(distributionId) ? (
                                    <TextField disabled size="s" />
                                ) : (
                                    <ControlNumberField
                                        key={`${item.id}-2-input`}
                                        name={`objectDistribution.${index}.cost`}
                                        control={control}
                                        size="s"
                                        decimalScale={0}
                                        disabled={isSystemCalculation || isCalcPerforming}
                                    />
                                )}
                            </GridItem>
                        </Fragment>
                    );
                })}
            </Grid>
        </>
    );
};

BimDistributionOtherAllocation.displayName = 'BimDistributionOtherAllocation';
